/**
 * Where the moon is, and how much of it is lit (T116, **D60**).
 *
 * `sun.ts` is the model this follows, deliberately — same coordinate
 * conventions, same horizontal triangle, same "north is −z" rule.
 *
 * ## ⚠️ This is a SIMPLIFIED model, and that is a decision rather than a corner cut
 *
 * The real Moon is genuinely hard: its orbit is inclined about **5.1°** to the
 * ecliptic, that inclination's nodes go round in **18.6 years**, the orbit is
 * eccentric and precesses, and a full treatment runs to dozens of periodic terms.
 *
 * **We model the mean moon** — the right phase, the right rising time, the right
 * general path — and ignore the rest. 🔴 **The justification is D2:** nobody will
 * ever check our lunar declination against an ephemeris, and the difference is
 * invisible in a game whose day lasts twenty minutes. What people *will* notice
 * is a moon that is full on the wrong night or does not move across the sky, and
 * this gets both of those right.
 *
 * **What the simplification costs, stated so nobody rediscovers it as a bug:**
 * the real moon's declination swings between about ±18.3° and ±28.6° depending
 * on where the nodes are; ours swings a fixed **±23.44°**, the ecliptic's own
 * tilt. So our moon rides slightly too low in some years and slightly too high in
 * others — **and since the calendar has no year, there is nothing to compare it
 * to anyway.**
 */

import { DAYS_PER_YEAR, type WorldTime } from './clock.ts';
import { WORLD_LATITUDE, WORLD_LONGITUDE } from './sun.ts';

const RAD = Math.PI / 180;

/**
 * The synodic month — new moon to new moon — in days.
 *
 * **29.530588853.** This is the number that makes the moon *not* line up with
 * anything: D41's calendar is 365 days, and 365 ÷ 29.53 is **12.36**, so a year
 * does not contain a whole number of moons. **That is why the phase runs off the
 * never-resetting `absoluteDay` and not the calendar** — see below.
 */
export const SYNODIC_MONTH_DAYS = 29.530588853;

/** The tilt of the Earth's axis, degrees. */
const OBLIQUITY = 23.44;

/**
 * What the moon's phase is on the world's first day.
 *
 * ⚠️ **Chosen, not derived.** The calendar has no year (**D41**), so there is no
 * real date to anchor a real lunation to — the epoch is ours to pick and any
 * choice is equally "correct".
 *
 * **0.40 is a waxing gibbous, about 90% lit, going full within four days.** It is
 * picked so **a new world's first night is a bright one**: the moon is the only
 * light there is until fire exists (T110), and opening the game on the darkest
 * night of the month would be a poor introduction to a mechanic that is supposed
 * to be a gift.
 */
const PHASE_AT_DAY_ONE = 0.4;

/**
 * How far through the lunation we are: **0 and 1 are new, 0.5 is full.**
 *
 * 🔴 **Driven by `absoluteDay`, which never resets — this is D60 and it is the
 * whole point.** The calendar repeats every 365 days; the moon must not, or
 * every 25 May would carry an identical moon for ever, which is both wrong and
 * the sort of thing somebody notices after a month of play and cannot explain.
 *
 * ⚠️ **Uses `dayFraction` too**, so the phase creeps through the day rather than
 * stepping at midnight. Over a 20-minute game day the moon grows by 3.4% of a
 * lunation, which is small but visible at the terminator on a big disc.
 */
export function moonPhase(time: WorldTime): number {
  const days = time.absoluteDay - 1 + time.dayFraction;
  const raw = PHASE_AT_DAY_ONE + days / SYNODIC_MONTH_DAYS;
  /* `% 1` keeps the sign of the dividend, and absoluteDay is always ≥ 1, so this
     cannot go negative — but the guard costs nothing and a negative phase would
     silently invert the terminator. */
  return ((raw % 1) + 1) % 1;
}

/**
 * How much of the disc is lit, 0 (new) to 1 (full).
 *
 * The illuminated fraction of a sphere lit from one side and seen at an angle is
 * `(1 − cos θ) / 2`, where θ is the elongation from the sun. **It is not linear
 * in the phase**, which matters for the light: a "half moon" is 50% lit by area
 * but the moon is at half phase for only a moment either side of it, and the
 * gibbous end of the cycle is much longer than a linear reading suggests.
 */
export function moonIllumination(time: WorldTime): number {
  return (1 - Math.cos(2 * Math.PI * moonPhase(time))) / 2;
}

/** Where the moon is in the sky. Same shape and axes as {@link SunPosition}. */
export interface MoonPosition {
  /** Degrees above the horizon; negative is below it. */
  readonly elevation: number;
  /** Degrees clockwise from north. */
  readonly azimuth: number;
  /** Unit vector from the ground toward the moon. **North is −z.** */
  readonly direction: { readonly x: number; readonly y: number; readonly z: number };
  /** 0 new … 1 full. The same number {@link moonIllumination} returns. */
  readonly illumination: number;
  /** 0…1 through the lunation, for drawing which side is lit. */
  readonly phase: number;
}

/**
 * Where the moon is at a moment of the world's clock.
 *
 * **The trick that keeps this simple, and it is exact rather than approximate:
 * the moon's hour angle is the sun's, less the elongation.** At new moon the two
 * are together in the sky; at full moon they are twelve hours apart, so the moon
 * rises as the sun sets. That single subtraction produces the behaviour everybody
 * actually recognises — **a moon that rises about 50 minutes later each day** —
 * without any orbital mechanics at all. (360° ÷ 29.53 days ≈ 12.2° per day ≈ 49
 * minutes.)
 */
export function moonPosition(
  time: WorldTime,
  latitude = WORLD_LATITUDE,
  longitude = WORLD_LONGITUDE,
): MoonPosition {
  const phase = moonPhase(time);
  const hoursUtc = time.dayFraction * 24;

  /* The sun's own hour angle, the same way `sunPosition` builds it. The equation
     of time is deliberately left out here: it is a ±16-minute correction to a
     body whose position we are already simplifying by degrees. */
  const sunHourAngle = ((hoursUtc * 60) / 4 - 180 + longitude) * RAD;
  const hourAngle = sunHourAngle - 2 * Math.PI * phase;

  /* The moon's ecliptic longitude: the sun's, plus the elongation. */
  const g = ((2 * Math.PI) / DAYS_PER_YEAR) * (time.dayOfYear - 1 + (hoursUtc - 12) / 24);
  /* A mean sun is enough to hang the moon off — see the header. */
  const sunLongitude = g + 4.9 + 0.033 * Math.sin(g);
  const moonLongitude = sunLongitude + 2 * Math.PI * phase;

  /* ⚠️ The 5.1° orbital inclination is deliberately ignored: the moon is put on
     the ecliptic. See the header for what that costs. */
  const declination = Math.asin(Math.sin(OBLIQUITY * RAD) * Math.sin(moonLongitude));

  const latR = latitude * RAD;
  const sinElevation =
    Math.sin(latR) * Math.sin(declination) +
    Math.cos(latR) * Math.cos(declination) * Math.cos(hourAngle);
  const elevationR = Math.asin(Math.min(1, Math.max(-1, sinElevation)));

  const cosAzimuth =
    (Math.sin(declination) - Math.sin(elevationR) * Math.sin(latR)) /
    (Math.cos(elevationR) * Math.cos(latR));
  let azimuth = Math.acos(Math.min(1, Math.max(-1, cosAzimuth))) / RAD;
  /* `acos` only ever answers 0…180 and cannot tell east from west. */
  if (Math.sin(hourAngle) > 0) azimuth = 360 - azimuth;

  const horizontal = Math.cos(elevationR);

  return {
    elevation: elevationR / RAD,
    azimuth,
    direction: {
      x: Math.sin(azimuth * RAD) * horizontal,
      y: Math.sin(elevationR),
      z: -Math.cos(azimuth * RAD) * horizontal,
    },
    illumination: (1 - Math.cos(2 * Math.PI * phase)) / 2,
    phase,
  };
}
